import React, { Component } from 'react';
import Select from 'react-select';
import InfiniteScroll from 'react-infinite-scroller';
import { WidgetHelper } from '../WidgetHelper';
import {
    IPublication,
    IDropDownValue,
    IPublicationRequest,
} from '../../Models';
import { PublicationService } from '../../Services';
import { PublicationLink } from '../../Components/PublicationLink';

interface IPublicationSearchWidgetProps {
    configId: number;
    title: string;
    dictResultLabel: string;
    dictResultsLabel: string;
    dictSearchLabel: string;
    dictThemesLabel: string;
    dictNoResultTitle: string;
    dictNoResultDescription: string;
}

interface IPublicationSearchWidgetState {
    hasMoreResult: boolean;
    pageCurrent: number;
    searchResultCount: number;
    searchResultItems: IPublication[];
    searchString: string | null;
    searchStringCurrent: string | null;
    themes: IDropDownValue[];
    themeSelected: IDropDownValue | undefined;
    isLoading: boolean;
}

export class PublicationSearchWidget extends Component<
    IPublicationSearchWidgetProps,
    IPublicationSearchWidgetState
> {
    private cancellationTokenPublication: AbortController;
    private cancellationTokenTheme: AbortController;
    private currentTheme: IDropDownValue | undefined;

    constructor(props: IPublicationSearchWidgetProps) {
        super(props);

        this.state = {
            hasMoreResult: false,
            pageCurrent: 0,
            searchResultCount: 0,
            searchResultItems: [],
            searchString: null,
            searchStringCurrent: null,
            themes: [],
            themeSelected: undefined,
            isLoading: true,
        };

        this.getAllTheme();
        this.getSearchResult();

        this.handleChangeSearchString =
            this.handleChangeSearchString.bind(this);
        this.handleChangeTheme = this.handleChangeTheme.bind(this);
        this.handleLoadMore = this.handleLoadMore.bind(this);
        this.handleSubmit = this.handleSubmit.bind(this);
    }

    async getAllTheme() {
        if (this.cancellationTokenTheme) {
            this.cancellationTokenTheme.abort();
        }

        this.cancellationTokenTheme = new AbortController();

        try {
            const themes = await PublicationService.getAllTheme(
                this.props.configId,
                this.cancellationTokenTheme.signal
            );
            this.setState({
                themes: themes.all
                    .sort((t) => t.sortOrder)
                    .map((t) => {
                        return {
                            value: t.id.toString(),
                            label: t.name,
                        };
                    }),
            });
        } catch (e) {
            if (!(e instanceof DOMException) || e.name !== 'AbortError') {
                throw e;
            }
        }
    }

    // to get the themes associated to a publication
    getPublicationTheme(themeIds: number[]): string[] {
        const themes = themeIds.map((id) => {
            const theme = this.state.themes.find((t) => Number(t.value) == id);

            return theme ? theme.label! : '';
        });

        return themes.filter((t) => t.length);
    }

    getPublicationFilter(): IPublicationRequest {
        return {
            configId: this.props.configId,
            searchString:
                this.state.searchStringCurrent &&
                this.state.searchStringCurrent.length > 0
                    ? this.state.searchStringCurrent
                    : null,
            themeId: this.currentTheme ? Number(this.currentTheme.value) : null,
            pageNumber: this.state.pageCurrent,
        };
    }

    async getSearchResult() {
        if (this.cancellationTokenPublication) {
            this.cancellationTokenPublication.abort();
        }

        this.cancellationTokenPublication = new AbortController();
        const searchParams = this.getPublicationFilter();

        try {
            const result = await PublicationService.search(
                searchParams,
                this.cancellationTokenPublication.signal
            );
            result.publications.forEach((publication) => {
                // transform Date to Date (javascript funny life :))
                publication.date = new Date(publication.date);
            });
            const resultItem = this.state.searchResultItems.concat(
                result.publications
            );

            this.setState({
                hasMoreResult: resultItem.length < result.total,
                pageCurrent: result.pageNumber + 1,
                searchResultItems: resultItem,
                searchResultCount: result.total,
                isLoading: false,
            });
        } catch (e) {
            if (!(e instanceof DOMException) || e.name !== 'AbortError') {
                throw e;
            }
        }
    }

    handleChangeSearchString(event: any) {
        this.setState({ searchString: event.target.value });
    }

    handleChangeTheme(theme: IDropDownValue) {
        this.setState({ themeSelected: theme });
    }

    handleLoadMore() {
        this.getSearchResult();
    }

    handleSubmit(event: any) {
        event.preventDefault();

        // set currentTheme only when hit submit
        this.currentTheme = this.state.themeSelected
            ? this.state.themeSelected
            : undefined;

        this.setState(
            {
                hasMoreResult: false,
                pageCurrent: 0,
                searchResultCount: 0,
                searchResultItems: [],
                themeSelected: this.currentTheme,
                searchStringCurrent: this.state.searchString,
                isLoading: true,
            },
            this.getSearchResult
        );
    }

    render() {
        return (
            <>
                <div className="banner banner--colored banner--colored__publications">
                    <div className="container">
                        <div className="row">
                            <div className="col-12 col-lg-8 offset-lg-2">
                                <h1>{this.props.title}</h1>
                                <form
                                    className="job-search job-search--global"
                                    onSubmit={this.handleSubmit}
                                    data-skip-captcha="true"
                                >
                                    <div className="form-group">
                                        <label
                                            htmlFor="keywordInput"
                                            className="sr-only sr-only-focusable"
                                        >
                                            {this.props.dictSearchLabel}
                                        </label>
                                        <div className="search">
                                            <input
                                                id="keywordInput"
                                                type="text"
                                                name="search"
                                                value={
                                                    this.state.searchString
                                                        ? this.state
                                                              .searchString
                                                        : ''
                                                }
                                                placeholder={`${this.props.dictSearchLabel}...`}
                                                onChange={
                                                    this
                                                        .handleChangeSearchString
                                                }
                                            />
                                        </div>
                                    </div>
                                    {this.state.themes.length > 0 && (
                                        <div className="form-group">
                                            <label
                                                id="themeSelectLabel"
                                                htmlFor="themeSelect"
                                                className="sr-only sr-only-focusable"
                                            >
                                                {this.props.dictThemesLabel}
                                            </label>
                                            <div className="field-icon objectif">
                                                <Select
                                                    id="themeSelect"
                                                    aria-labelledby="themeSelectLabel"
                                                    className="domain-select-container"
                                                    classNamePrefix="domain-select"
                                                    isClearable={true}
                                                    isSearchable={false}
                                                    placeholder={
                                                        this.props
                                                            .dictThemesLabel
                                                    }
                                                    onChange={
                                                        this.handleChangeTheme
                                                    }
                                                    options={this.state.themes}
                                                    value={
                                                        this.state.themeSelected
                                                    }
                                                />
                                            </div>
                                        </div>
                                    )}
                                    <div className="form-group submit">
                                        <button
                                            type="submit"
                                            className="gtm-publication-search theme-reactive"
                                        >
                                            <i className="icon-search"></i>
                                            {this.props.dictSearchLabel}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="container">
                    <div className="row">
                        <div className="col-12 col-lg-8 offset-lg-2">
                            {this.state.isLoading && (
                                <div className="lds-ripple">
                                    <div></div>
                                    <div></div>
                                </div>
                            )}
                            {this.state.searchResultCount == 0 &&
                                !this.state.isLoading && (
                                    <div className="empty-state">
                                        <i className="icon-no-results"></i>
                                        <p>
                                            <b>
                                                {this.props.dictNoResultTitle}
                                            </b>
                                        </p>
                                        <p>
                                            {this.props.dictNoResultDescription}
                                        </p>
                                    </div>
                                )}
                            {this.state.searchResultCount > 0 && (
                                <div className="results-topbar">
                                    <span className="number-results">
                                        {this.state.searchResultCount}{' '}
                                        {this.state.searchResultCount > 1
                                            ? this.props.dictResultsLabel
                                            : this.props.dictResultLabel}
                                    </span>
                                </div>
                            )}
                            <InfiniteScroll
                                className="tiles"
                                hasMore={this.state.hasMoreResult}
                                loader={
                                    <div key={'loader'} className="lds-ripple">
                                        <div></div>
                                        <div></div>
                                    </div>
                                }
                                loadMore={this.handleLoadMore}
                            >
                                {this.state.searchResultItems.map(
                                    (publication, index) => (
                                        <PublicationLink
                                            {...publication}
                                            themes={this.getPublicationTheme(
                                                publication.themeIds
                                            )}
                                            key={index}
                                        />
                                    )
                                )}
                            </InfiniteScroll>
                        </div>
                    </div>
                </div>
            </>
        );
    }
}

WidgetHelper.register(PublicationSearchWidget, 'publication-search-widget');
